// Mappers for Monthly Cuts
import type {
  LC_GetMonthlyCutsOut,
  MonthlyCut,
  MonthlyCutsResult,
} from '../types';
import { formatToDdMmYyyy } from '@/utils';

// Constants
const MONTHLY_CUTS_LOOKBACK_MONTHS = 60;

// Map Spanish month names to numbers
const SPANISH_MONTHS: Record<string, number> = {
  'Enero': 1,
  'Febrero': 2,
  'Marzo': 3,
  'Abril': 4,
  'Mayo': 5,
  'Junio': 6,
  'Julio': 7,
  'Agosto': 8,
  'Septiembre': 9,
  'Octubre': 10,
  'Noviembre': 11,
  'Diciembre': 12,
};

export function mapMonthlyCut(transport: any): MonthlyCut {
  // Handle both English (API schema) and Spanish (API example) property names
  const id = transport.monthlyCutId ?? transport.idCorteMensual;
  const year = transport.year ?? transport.anio;
  const month = transport.month ?? transport.mes;
  const description = transport.description ?? transport.descripcion;
  const startDate = transport.startDate ?? transport.fechaInicio;
  const endDate = transport.endDate ?? transport.fechaFin;

  // Parse year (can be string number like "2025")
  let yearNum: number | null = Number(year);
  if (isNaN(yearNum)) yearNum = null;

  // Parse month (can be Spanish text like "Marzo" or number)
  let monthNum: number | null = null;
  if (typeof month === 'string' && SPANISH_MONTHS[month]) {
    // Month is Spanish text name (e.g., "Marzo")
    monthNum = SPANISH_MONTHS[month];
  } else {
    // Month might be a number or numeric string
    monthNum = Number(month);
    if (isNaN(monthNum)) monthNum = null;
  }

  // Fallback to startDate only if year or month are still missing/invalid
  if (
    (!yearNum || !monthNum) &&
    startDate
  ) {
    const dateObj = new Date(startDate);
    if (!isNaN(dateObj.getTime())) {
      if (!yearNum) yearNum = dateObj.getFullYear();
      if (!monthNum) monthNum = dateObj.getMonth() + 1;
    }
  }

  const monthStr =
    monthNum !== null ? String(monthNum).padStart(2, '0') : '00';
  const code = `${yearNum !== null ? yearNum : '0000'}${monthStr}`;

  return {
    monthlyCutId: id,
    id: String(id),
    code: code,
    label: description,
    startDate: formatToDdMmYyyy(startDate),
    endDate: formatToDdMmYyyy(endDate),
    year: yearNum,
    month: monthNum,
  };
}

export function mapMonthlyCutsResult(
  transport: LC_GetMonthlyCutsOut,
): MonthlyCutsResult {
  return {
    cuts: (transport.monthlyCuts || []).map(mapMonthlyCut),
    total: transport.totalCuts,
    oldest: transport.oldestPeriod,
    newest: transport.mostRecentPeriod,
  };
}

// --- Utility Functions for Monthly Cuts Transformation ---

/**
 * Filters monthly cuts to only include those within the last N months
 * @param cuts - Array of monthly cuts to filter
 * @param lookbackMonths - Number of months to look back (default 60)
 * @returns Filtered array of monthly cuts
 */
export function filterMonthlyCutsByRange(
  cuts: MonthlyCut[],
  lookbackMonths: number = MONTHLY_CUTS_LOOKBACK_MONTHS,
): MonthlyCut[] {
  const now = new Date();
  const cutoffDate = new Date(
    now.getFullYear(),
    now.getMonth() - (lookbackMonths - 1),
    1,
  );

  return cuts.filter(cut => {
    // Filter out cuts without valid year/month
    if (cut.year === null || cut.month === null) return false;

    // Filter by lookback range
    const cutMonthDate = new Date(cut.year, cut.month - 1, 1);
    return cutMonthDate >= cutoffDate;
  });
}

/**
 * Generates year dropdown options from available monthly cuts
 * @param cuts - Array of monthly cuts
 * @returns Array of year options sorted in descending order
 */
export function generateYearOptions(
  cuts: MonthlyCut[],
): Array<{ value: string; label: string }> {
  const years = new Set<number>();
  cuts.forEach(cut => {
    if (cut.year) years.add(cut.year);
  });

  return Array.from(years)
    .sort((a, b) => b - a) // Descending order
    .map(year => ({
      value: year.toString(),
      label: year.toString(),
    }));
}

/**
 * Generates month dropdown options for a selected year
 * @param cuts - Array of monthly cuts
 * @param selectedYear - Selected year to filter months by
 * @returns Array of month options sorted in descending order
 */
export function generateMonthOptions(
  cuts: MonthlyCut[],
  selectedYear: number,
): Array<{ value: string; label: string }> {
  if (!selectedYear) return [];

  const months = new Set<number>();
  cuts.forEach(cut => {
    if (cut.year === selectedYear && cut.month) {
      months.add(cut.month);
    }
  });

  const REFERENCE_YEAR = 2000;
  return Array.from(months)
    .sort((a, b) => b - a) // Descending order
    .map(month => {
      const date = new Date(REFERENCE_YEAR, month - 1, 1);
      const monthName = date.toLocaleString('es-CL', { month: 'long' });
      return {
        value: month.toString(),
        label: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      };
    });
}

